import posthog from "posthog-js";
import {
  experiments,
  isExperimentKey,
  isVariantOf,
  resolveVariant,
  type ExperimentKey,
  type Variant,
} from "./experiments";
import { isPaywallId, isPlanIdOf, type PaywallId } from "./paywalls";

/* ---------------------------------------------------------------------------
   The closed marketing vocabulary.

   This app is a medication journal, so the payload allowlist is a product
   requirement rather than a lint rule: every property is checked against a
   registry before it can leave the tab, and anything unrecognised drops the
   whole event rather than being stripped and sent anyway. Silently sending a
   partial event is worse than sending none — it looks like the boundary held.
--------------------------------------------------------------------------- */

export type GrowthEvent =
  | "landing_viewed"
  | "quiz_started"
  | "quiz_progressed"
  | "quiz_completed"
  | "experiment_exposed"
  | "paywall_viewed"
  | "paywall_plan_selected"
  | "paywall_cta_clicked"
  | "app_store_cta_clicked";

export type Placement = "hero" | "navigation" | "faq" | "paywall";

export type GrowthProperties = {
  /** Always paired with `variant`, so one arm name cannot mean two things. */
  experiment?: ExperimentKey;
  variant?: Variant;
  placement?: Placement;
  paywall_id?: PaywallId;
  /** Always paired with `paywall_id`, and must be a plan that offer has. */
  plan_id?: string;
  progress_bucket?: 25 | 50 | 75 | 100;
  funnel_version?: "web-v1";
};

const allowedProperties = new Set<keyof GrowthProperties>([
  "experiment",
  "variant",
  "placement",
  "paywall_id",
  "plan_id",
  "progress_bucket",
  "funnel_version",
]);

const allowedPlacements = new Set<Placement>(["hero", "navigation", "faq", "paywall"]);
const allowedProgressBuckets = new Set([25, 50, 75, 100]);

const forbiddenPropertyPattern =
  /answer|medication|dose|symptom|weight|height|birthday|birth|sex|gender|concern|craving|treatment|health|goal|activity/i;

let initialized = false;
let paused = false;
let pendingQuizStartProperties: GrowthProperties = {};
let projectKey = "";
let ingestHost = "https://us.i.posthog.com";

export function isGrowthPayloadAllowed(value: unknown): value is GrowthProperties {
  if (value === undefined) return true;
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const payload = value as Record<string, unknown>;

  return Object.entries(payload).every(([key, property]) => {
    if (forbiddenPropertyPattern.test(key)) return false;
    if (!allowedProperties.has(key as keyof GrowthProperties)) return false;
    // Scalars only: a nested object is a place for an answer to hide.
    if (property === null || typeof property === "object" || typeof property === "function") {
      return false;
    }

    switch (key as keyof GrowthProperties) {
      case "experiment":
        return isExperimentKey(property);
      case "variant":
        // An arm name only means something beside the experiment that declares
        // it, and "control" belongs to every experiment at once.
        return isExperimentKey(payload.experiment) && isVariantOf(payload.experiment, property);
      case "placement":
        return allowedPlacements.has(property as Placement);
      case "paywall_id":
        return isPaywallId(property);
      case "plan_id":
        return isPlanIdOf(payload.paywall_id, property);
      case "progress_bucket":
        return allowedProgressBuckets.has(property as number);
      case "funnel_version":
        return property === "web-v1";
    }
  });
}

export function initializeGrowthAnalytics() {
  const key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY?.trim();
  if (!key || initialized) return false;

  posthog.init(key, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
    defaults: "2026-05-30",
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    disable_surveys: true,
    person_profiles: "never",
    persistence: "memory",
    request_batching: false,
  });
  projectKey = key;
  ingestHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";
  initialized = true;
  return true;
}

/**
 * Stops capture without tearing the client down, for surfaces that render real
 * funnel screens outside a real visit. The /dev board mounts every screen at
 * once, and each one would otherwise report itself as viewed.
 */
export function setGrowthCapturePaused(next: boolean) {
  paused = next;
}

export function captureGrowthEvent(event: GrowthEvent, properties: GrowthProperties = {}) {
  const payload = { funnel_version: "web-v1" as const, ...properties };
  if (!initialized || paused || !isGrowthPayloadAllowed(payload)) return false;
  void fetch(`${ingestHost}/capture/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      api_key: projectKey,
      event,
      // Share the anonymous, memory-only ID used for flag evaluation so an
      // exposure and its conversion belong to the same experiment visitor.
      properties: { distinct_id: posthog.get_distinct_id(), ...payload },
    }),
    keepalive: true,
  });
  return true;
}

/** PostHog's experiment engine requires this protocol event to form cohorts.
 * It is emitted explicitly so its payload remains as closed as our product
 * events: anonymous ID, registered flag, and registered arm only. */
export function captureExperimentExposure<K extends ExperimentKey>(
  experiment: K,
  variant: Variant<K>,
) {
  if (!initialized || paused || !isVariantOf(experiment, variant)) return false;
  void fetch(`${ingestHost}/capture/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      api_key: projectKey,
      event: "$feature_flag_called",
      properties: {
        distinct_id: posthog.get_distinct_id(),
        $feature_flag: experiments[experiment].flag,
        $feature_flag_response: variant,
      },
    }),
    keepalive: true,
  });
  return true;
}

export function prepareQuizStart(properties: GrowthProperties = {}) {
  if (!isGrowthPayloadAllowed(properties)) return false;
  pendingQuizStartProperties = properties;
  return true;
}

export function takeQuizStartProperties() {
  const properties = pendingQuizStartProperties;
  pendingQuizStartProperties = {};
  return properties;
}

/* ------------------------------------------------------------------- Flags */

/**
 * The variant for one experiment, now and on every re-evaluation.
 *
 * The two-second deadline is the point where waiting for a flag costs more than
 * showing the control arm: the callback fires with control, and fires again if
 * the flag turns up late. Callers that must not swap copy under a reader hold
 * their own placeholder until the first call rather than guessing an arm.
 */
export function observeVariant<K extends ExperimentKey>(
  key: K,
  onVariant: (variant: Variant<K>) => void,
) {
  if (!initialized) {
    onVariant(resolveVariant(key, undefined));
    return () => {};
  }

  const { flag } = experiments[key];
  let resolved = false;
  const emit = () => {
    resolved = true;
    onVariant(resolveVariant(key, posthog.getFeatureFlag(flag, { send_event: false })));
  };

  if (posthog.getFeatureFlag(flag, { send_event: false }) !== undefined) emit();
  const timeout = window.setTimeout(() => {
    if (!resolved) emit();
  }, 2_000);
  const unsubscribe = posthog.onFeatureFlags(() => {
    window.clearTimeout(timeout);
    emit();
  });

  return () => {
    window.clearTimeout(timeout);
    unsubscribe();
  };
}
